/*
 Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights Reserved.

 Licensed under the Amazon Software License (the "License"). You may not use this file
 except in compliance with the License. A copy of the License is located at

 http://aws.amazon.com/asl/

 or in the "license" file accompanying this file. This file is distributed on an "AS IS"
 BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. See the
 License for the specific language governing permissions and limitations under the License.
 */

/* eslint no-console: ["error", { allow: ["info", "warn", "error"] }] */
/* global AudioContext CustomEvent document Event navigator window */

// wav encoder worker - uses webpack worker loader
import WavWorker from './wav-worker';
import * as Sentry from '@sentry/browser';
import {isiPad} from '../../services/environment';
import {logSessionInfo} from '../../services/logger';
import { logSentryException, isIPadBelowVersion } from '../../services/util';
import { FEATURE_FLAGS } from '../../constants/featureFlags';
import { getUserData } from '../../services/API';
import { globalContext, constructionError } from './audioContext';
// Inline loader forces a standalone URL so AudioWorklet code is not evaluated in the main bundle.
import audioProcessorWorkletUrl from '!!file-loader?name=build/[name].[hash].[ext]!./audio-processor.worklet.js';

if (!globalContext) {
  const reason = constructionError ? constructionError.message : 'AudioContext constructor unavailable';
  throw new Error(`Web Audio API not supported: ${reason}`);
}
// Re-export so existing consumers (Speech.js, etc.) keep importing from recorder.
export { globalContext };
let _preCheckStep = null;
let MAX_RESUME_CONTEXT_ERROR_LOG_COUNT = 3; // max number of times to log resume error before giving up

// Capture URL param at module load time to ensure it's available throughout the session
const _initialUrlParams = new URLSearchParams(window.location.search);
const _disableAudioPreProcessing = _initialUrlParams.get('disableAudioPreProcessing') === 'true';

// ============================================
// AUDIO FILE INPUT CONFIGURATION
// ============================================
// Hardcode the file path here. If the file exists at this path, 
// it will be used instead of microphone input.
// Set to null or empty string to always use microphone.
// Example: '/path/to/your/audio-file.wav'
const LOCAL_FILE_PATH = '';
const AUDIO_FILE_PATH = window.location.host.startsWith('localhost') ? LOCAL_FILE_PATH : null; // Change this to your audio file path

// Internal state for audio file mode
let _audioFileMode = false;
let _audioFileBuffer = null;
let _audioFilePlaybackPosition = 0;
let _audioFilePlaybackStartTime = 0; // Track when current playback segment started

/**
 * Returns whether audio processing constraints are enabled.
 * Checks the URL param first (disableAudioPreProcessing), then falls back to feature flag.
 * @returns {boolean} true if audio processing is enabled
 */
export function isAudioProcessingEnabled() {
  return !_disableAudioPreProcessing && FEATURE_FLAGS.ENABLE_AUDIO_PROCESSING_CONSTRAINTS;
}

export function setPreCheckStep(step) {
  _preCheckStep = step;
}

export function resetResumeErrorLogCount() {
  MAX_RESUME_CONTEXT_ERROR_LOG_COUNT = 3;
};

document.addEventListener('visibilitychange', () => {
  const shouldShowAlert = isiPad();
  console.info('visibility change triggered in recorder. hidden:', document.hidden);
  if (document.hidden) {
    globalContext.suspend()
    .then(()=>{
      logSessionInfo('window_hidden',{});
      console.log("audio suspended successfully");
    })
    .catch((err)=>{
      console.log("error suspending",err);
      Sentry.captureMessage("Error Suspending");
      logSessionInfo('error_suspending',err);
    })
    if (shouldShowAlert) {
      logSessionInfo('display_ipad_alert_box',{});
      alert("Looks like you moved away from the page. Click the close button below to resume");
    }
  } else {
    try {
      if (shouldShowAlert) {
        if (Howler?.ctx) { Howler.ctx.resume?.(); }
      }
      globalContext.resume()
      .then(()=>{
        resetResumeErrorLogCount();
        logSessionInfo('window_unhidden',{});
        console.log("audio resumed successfully");
      })
      .catch((err)=>{
        console.log("error resuming",err);
        Sentry.captureMessage("Error Resuming", _preCheckStep ? {
          tags: { preCheckStep: _preCheckStep }
        } : undefined);
          logSessionInfo('error_resuming',err);
      });
    } catch (error) {
      if (MAX_RESUME_CONTEXT_ERROR_LOG_COUNT === 0) {
        return;
      } // do not log more than 3 errors for a single user session
      MAX_RESUME_CONTEXT_ERROR_LOG_COUNT--;
      console.log("error resuming",error);
      Sentry.captureMessage("Error calling Resume", _preCheckStep ? {
        tags: { preCheckStep: _preCheckStep }
      } : undefined);
      logSessionInfo('error_resuming',error);
    }
  }
});

const getConstraints = (iPad) => {
  const enableAudioProcessing = isAudioProcessingEnabled();
  
  if (enableAudioProcessing) {
    // Check if iPad is below version 18
    if (isIPadBelowVersion(18)) {
      // Use old constraints for iPads below version 18
      return {
        audio: {
          autoGainControl: false,
          echoCancellation: false,
          noiseSuppression: false,
        },
      };
    }
    
    // When feature flag is enabled, use the same constraints for both iPad and non-iPad
    return {
      audio: {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
        voiceIsolation: true,
      },
    };
  }

  // Original constraints when feature flag is disabled
  if (iPad) {
    return {
      audio: {
        autoGainControl: false,
        echoCancellation: false,
        noiseSuppression: false,
      },
    };
  }

  return {
    audio: {
      optional: [{
        echoCancellation: false,
      }],
    },
  };
};


let globalStream = null;
const iPad = isiPad();

export function initAudio(){
  if(iPad){
    if(globalStream) return Promise.resolve(true);
    return navigator.mediaDevices.getUserMedia(getConstraints(iPad))
    .then((stream) => {
      globalStream=stream;
      return Promise.resolve(true);
    })
    .catch((err)=>{
      console.warn("Could not get iPad microphone",err.name,err.message);
      logSentryException(new Error('iPad microphone access failure'), 'Could not get iPad microphone', { originalError: err?.message, originalStack: err?.stack });
      return Promise.resolve(false);
    });
  }else{
    return Promise.resolve(true);
  }
}

export function prepareMicForPlayback() {
  if (globalStream && iPad) {
    globalStream.getAudioTracks().forEach(track => {
      track.enabled = false;
    });
  }
}

export function prepareMicForRecord() {
  if (globalStream && iPad) {
    globalStream.getAudioTracks().forEach(track => {
      track.enabled = true;
    });
  }
}

/**
 * Lex Recorder Module
 * Based on Recorderjs. It sort of mimics the MediaRecorder API.
 * @see {@link https://github.com/mattdiamond/Recorderjs}
 * @see {@https://github.com/chris-rudmin/Recorderjs}
 * @see {@https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder}
 */

/**
 * Class for Lex audio recording management.
 *
 * This class is used for microphone initialization and recording
 * management. It encodes the mic input into wav format.
 * It also monitors the audio input stream (e.g keeping track of volume)
 * filtered around human voice speech frequencies to look for silence
 */
export default class {
  /* eslint no-underscore-dangle: ["error", { "allowAfterThis": true }] */

  /**
   * Constructs the recorder object
   *
   * @param {object} - options object
   *
   * @param {string} options.mimeType - Mime type to use on recording.
   *   Only 'audio/wav' is supported for now. Default: 'aduio/wav'.
   *
   * @param {boolean} options.autoStopRecording -  Controls if the recording
   *   should automatically stop on silence detection. Default: true.
   *
   * @param {number} options.recordingTimeMax - Maximum recording time in
   *   seconds. Recording will stop after going for this long. Default: 8.
   *
   * @param {number} options.recordingTimeMin - Minimum recording time in
   *   seconds. Used before evaluating if the line is quiet to allow initial
   *   pauses before speech. Default: 2.
   *
   * @param {boolean} options.recordingTimeMinAutoIncrease - Controls if the
   *   recordingTimeMin should be automatically increased (exponentially)
   *   based on the number of consecutive silent recordings.
   *   Default: true.
   *
   * @param {number} options.quietThreshold - Threshold of mic input level
   *   to consider quiet. Used to determine pauses in input this is measured
   *   using the "slow" mic volume. Default: 0.001.
   *
   * @param {number} options.quietTimeMin - Minimum mic quiet time (normally in
   *   fractions of a second) before automatically stopping the recording when
   *   autoStopRecording is true. In reality it takes a bit more time than this
   *   value given that the slow volume value is a decay. Reasonable times seem
   *   to be between 0.2 and 0.5. Default: 0.4.
   *
   * @param {number} options.volumeThreshold - Threshold of mic db level
   *   to consider quiet. Used to determine pauses in input this is measured
   *   using the "max" mic volume. Smaller values make the recorder auto stop
   *   faster. Default: -75
   *
   * @param {bool} options.useBandPass - Controls if a band pass filter is used
   *   for the microphone input. If true, the input is passed through a second
   *   order bandpass filter using AudioContext.createBiquadFilter:
   *   https://developer.mozilla.org/en-US/docs/Web/API/AudioContext/createBiquadFilter
   *   The bandpass filter helps to reduce noise, improve silence detection and
   *   produce smaller audio blobs. However, it may produce audio with lower
   *   fidelity. Default: true
   *
   * @param {number} options.bandPassFrequency - Frequency of bandpass filter in
   *   Hz. Mic input is passed through a second order bandpass filter to remove
   *   noise and improve quality/speech silence detection. Reasonable values
   *   should be around 3000 - 5000. Default: 4000.
   *
   * @param {number} options.bandPassQ - Q factor of bandpass filter.
   *   The higher the vaue, the narrower the pass band and steeper roll off.
   *   Reasonable values should be between 0.5 and 1.5. Default: 0.707
   *
   * @param {number} options.bufferLength - Length of buffer used in audio
   *   processor. Should be in powers of two between 512 to 8196. Passed to
   *   script processor and audio encoder. Lower values have lower latency.
   *   Default: 2048.
   *
   * @param {number} options.numChannels- Number of channels to record.
   *   Default: 1 (mono).
   *
   * @param {number} options.requestEchoCancellation - Request to use echo
   *   cancellation in the getUserMedia call:
   *   https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackConstraints/echoCancellation
   *   Default: true.
   *
   * @param {bool} options.useAutoMuteDetect - Controls if the recorder utilizes
   *   automatic mute detection.
   *   Default: true.
   *
   * @param {number} options.muteThreshold - Threshold level when mute values
   *   are detected when useAutoMuteDetect is enabled. The higher the faster
   *   it reports the mic to be in a muted state but may cause it to flap
   *   between mute/unmute. The lower the values the slower it is to report
   *   the mic as mute. Too low of a value may cause it to never report the
   *   line as muted. Works in conjuction with options.quietTreshold.
   *   Reasonable values seem to be between: 1e-5 and 1e-8. Default: 1e-7.
   *
   * @param {bool} options.encoderUseTrim - Controls if the encoder should
   *   attempt to trim quiet samples from the beginning and end of the buffer
   *   Default: true.
   *
   * @param {number} options.encoderQuietTrimThreshold - Threshold when quiet
   *   levels are detected. Only applicable when encoderUseTrim is enabled. The
   *   encoder will trim samples below this value at the beginnig and end of the
   *   buffer. Lower value trim less silence resulting in larger WAV files.
   *   Reasonable values seem to be between 0.005 and 0.0005. Default: 0.0008.
   *
   * @param {number} options.encoderQuietTrimSlackBack - How many samples to
   *   add back to the encoded buffer before/after the
   *   encoderQuietTrimThreshold. Higher values trim less silence resulting in
   *   larger WAV files.
   *   Reasonable values seem to be between 3500 and 5000. Default: 4000.
   */
  constructor(options = {}) {
    // track if init() has been called and completed
    this._initialized = false;
    
    this.initOptions(options);

    // event handler used for events similar to MediaRecorder API (e.g. onmute)
    this._eventTarget = document.createDocumentFragment();

    // encoder worker
    this._encoderWorker = new WavWorker();

    // worker uses this event listener to signal back
    // when wav has finished encoding
    this._encoderWorker.addEventListener(
      'message',
      evt => this._exportWav(evt.data),
    );
    this.isStreamInitInProgress = false; // flag to track if the stream is in the process of being initialized
    this.mutedTroubleshootTimeout = null; // timeout to display mic muted troubleshoot modal & clear if unmuted before it triggers
    this.maxInitCalledCount = 3; // max number of times to call init() before giving up after track statusChange
    resetResumeErrorLogCount(); // reset the max number of times to log resume error before giving up
    
    // Audio file playback state
    this._fileBufferSource = null;
    this._fileStreamDestination = null;
    this._audioFileDuration = 0;
    this._audioFilePlaybackPaused = false; // Track if playback is paused
    this._workletModuleReady = null;
    this._connectedMicVolumeProcessor = null;
  }

  /**
   * Initialize general recorder options
   *
   * @param {object} options - object with various options controlling the
   *   recorder behavior. See the constructor for details.
   */
  initOptions(options = {}) {
    // TODO break this into functions, avoid side-effects, break into this.options.*
    if (options.preset) {
      Object.assign(options, this._getPresetOptions(options.preset));
    }

    this.mimeType = options.mimeType || 'audio/wav';

    //recordingTimeMax controls the size of the audio segment.
    //NOTE: this is the minimum that the segment will be. If the overflow time
    // based on the value of bufferlength is > this value, the actual segment
    // size will be equal to bufferlength/44100. For example, a bufferlength
    // of 4096 will result in a segment time of ~ 92.9 mS. I've set the recording
    // time to be arbitrarily low so we don't get segment jitter so the bufferlength
    // really controls the segment timing.
    this.recordingTimeMax = options.recordingTimeMax || 0.05;
    this.recordingTimeMin = options.recordingTimeMin || 0;
    this.recordingTimeMinAutoIncrease =
      (typeof options.recordingTimeMinAutoIncrease !== 'undefined') ?
        !!options.recordingTimeMinAutoIncrease :
        true;

    //Speech Thresholds (in dB)
    this.loudThresholdDB = options.loudThresholdDB || -8;
    this.softThresholdDB = options.softThresholdDB || -40;

    // speech detection configuration
    this.autoStopRecording =
      (typeof options.autoStopRecording !== 'undefined') ?
        !!options.autoStopRecording :
        true;
    this.quietThreshold = options.quietThreshold || 0.001;
    this.quietTimeMin = options.quietTimeMin || 0.4;
    this.volumeThreshold = options.volumeThreshold || -75;

    // band pass configuration
    this.useBandPass =
      (typeof options.useBandPass !== 'undefined') ?
        !!options.useBandPass :
        true;
    // https://developer.mozilla.org/en-US/docs/Web/API/BiquadFilterNode
    this.bandPassFrequency = options.bandPassFrequency || 4000;
    // Butterworth 0.707 [sqrt(1/2)]  | Chebyshev < 1.414
    this.bandPassQ = options.bandPassQ || 0.707;

    // parameters passed to script processor and also used in encoder
    // https://developer.mozilla.org/en-US/docs/Web/API/AudioContext/createScriptProcessor
    this._bufferLength = options.bufferLength || 8192; //This results in ~186 ms segments (8192/44100)
    this.numChannels = options.numChannels || 1;

    this.requestEchoCancellation = false;
      /*(typeof options.requestEchoCancellation !== 'undefined') ?
        !!options.requestEchoCancellation :
        true;*/

    // automatic mute detection options
    this.useAutoMuteDetect =
      (typeof options.useAutoMuteDetect !== 'undefined') ?
        !!options.useAutoMuteDetect :
        true;
    this.muteThreshold = options.muteThreshold || 1e-7;

    // encoder options
    this.encoderUseTrim =
      (typeof options.encoderUseTrim !== 'undefined') ?
        !!options.encoderUseTrim :
        true;
    this.encoderQuietTrimThreshold =
      options.encoderQuietTrimThreshold || 0.0008;
    this.encoderQuietTrimSlackBack = options.encoderQuietTrimSlackBack || 4000;
    this.sentryMicErrorLogs = {
      reinit_exceeded_msg: "",
      reinit_error_msg: "",
      context_resume_error_msg: "",
      listeningStarted: [],
      listeningStopped: [],
      trackErrorsOnStart: [],
      trackErrorsOnStop: [],
      micTurnedGreen: [],
      micTurnedGrey: [],
    }
    this.preCheckStep = null; // Track current StudentPreChecks step for Sentry logging
  }

  _getPresetOptions(preset = 'low_latency') {
    this._presets = ['low_latency', 'speech_recognition'];

    if (this._presets.indexOf(preset) === -1) {
      console.error('invalid preset');
      return {};
    }

    const presets = {
      low_latency: {
        encoderUseTrim: true,
        useBandPass: true,
      },
      speech_recognition: {
        encoderUseTrim: false,
        useBandPass: false,
        useAutoMuteDetect: false,
      },
    };

    return presets[preset];
  }

  closeAudioContext(){
    globalContext.close();
  }

  /**
   * General init. This function should be called to initialize the recorder.
   *
   * @param {object} options - Optional parameter to reinitialize the
   *   recorder behavior. See the constructor for details.
   *
   * @return {Promise} - Returns a promise that resolves when the recorder is
   *   ready.
   */
  init() {
    this._state = 'inactive';
    this._instant = 0.0;
    this._slow = 0.0;
    this._clip = 0.0;
    this._nearClip = 0.0; // Near-clipping at 0.8 threshold (matches backend)
    this._maxVolume = -Infinity;
    this._minVolume = Infinity;
    this._maxClip = 0;
    this._recordingSession = {};

    this._isMicQuiet = true;
    this._isMicMuted = false;

    this._isSilentRecording = true;
    this._silentRecordingConsecutiveCount = 0;

    // Intelligibility metrics (simple - always calculated)
    this._zcrDuringClipping = 0.0;
    this._zcr = 0.0; // General zero-crossing rate (for static detection)
    this._spectralFlatness = 0.0;
    this._intelligibilitySeverity = 0.0;
    
    // Advanced audio quality metrics (requires feature flag)
    this._peakAmplitude = 0.0;
    this._crestFactorDB = 0.0;
    this._spectralRolloff = 0.0;
    this._highLowFreqRatio = 0.0;
    this._staticDetected = false;
    this._staticType = null; // 'low-band' or 'broadband' or null

    this._usingAudioWorklet = false;

    this._onstartHandler = null;
    this._onstopHandler = null;
    this._ondataavailableHandler = null;
    this._onerrorHandler = null;
    this._onquietHandler = null;
    this._onunquietHandler = null;

    // sets this._audioContext AudioContext object
    return this._initAudioContext()
      .then(() => {
        logSessionInfo('audio_context_sample_rate', { sampleRate: this._audioContext.sampleRate });
      })
      // Initializes AudioWorklet or ScriptProcessor fallback
      // sets this._micVolumeProcessor
      .then(() => {return this._initMicVolumeProcessor()})
      .then(() => {return this._initStream()})
      .then(() => {
        this._initialized = true;
        return Promise.resolve();
      });

  }

  /**
   * Start recording
   */
  start(recordingProps = {}) {
    // In file mode, start playing the audio file
    if (_audioFileMode) {
      this._startFilePlayback();
    } else {
      // Normal microphone mode
      prepareMicForRecord();
      // NEW: Verify track is actually enabled
      if (this._tracks && this._tracks[0]) {
        if (!this._tracks[0].enabled) {
          console.warn('VOICE_PRECHECKS_ISSUE: Track not enabled after prepareMicForRecord, forcing enable');
          this._tracks[0].enabled = true;
        }
        if (this._tracks[0].readyState !== 'live' && window.location.pathname.startsWith('/student/pre-checks') && !this.isStreamInitInProgress) {
          if (this.maxInitCalledCount === 0) {
            this.maxInitCalledCount = -1; // set to -1 to prevent infinite loop
            console.warn('VOICE_PRECHECKS_ISSUE: Max init called count reached, giving up');
            this.sentryMicErrorLogs.reinit_exceeded_msg = "Max init called count reached, giving up " +Date.now();
          } else if (this.maxInitCalledCount > 0) {
            console.warn('VOICE_PRECHECKS_ISSUE: Track not live on start');
            this._initStream(true);
            this.maxInitCalledCount--;
          }
        }
        
        // Check if track is muted
        if (this._tracks[0].muted) {
          console.warn('VOICE_PRECHECKS_ISSUE: Track is muted on start');
          this.mutedTroubleshootTimeout = setTimeout(() => {
            if (this._tracks?.[0]?.muted) this._eventTarget.dispatchEvent(new Event('trackmuted'));
          }, 3000); // wait for 3 seconds to make sure the track is not revived automatically
        }
      }

      if (iPad) {
        this.restartStream();
      }
    }

    /*if (this._state !== 'inactive' ||
      typeof this._stream === 'undefined') {
      console.warn('recorder start called out of state');
      return;
    }*/

   /* if(typeof this._stream === 'undefined')
    {
      console.warn('start out of state - stream: ',this);
      console.warn('typeof: ',typeof this._stream);
      return;
    }*/
    if(Object.keys(recordingProps).length > 0){
      this._recordingSession = recordingProps;
    }

    if(this._state !== 'inactive'){
      console.warn('start out of state - state: ',this._state);
      return;
    }

    if(recordingProps.clearHistory){
      this._maxVolume = -Infinity;
      this._minVolume = Infinity;
      this._maxClip = 0;
      this._slow = 0.0;
    }

    this._state = 'recording';

    this._recordingStartTime = this._audioContext.currentTime;
    this._eventTarget.dispatchEvent(new Event('start'));

    //This is necessary because Chrome now suspends audio contexts before
    // there is user interaction.
    if(this._audioContext.state !== 'running'){
      console.warn('mic volume was not running', this._audioContext.state); //leave in for troubleshooting bad audio
      const resumeResult = this._audioContext.resume();
      if (resumeResult instanceof Promise) {
        resumeResult.catch((err)=>{
          this.sentryMicErrorLogs.context_resume_error_msg = "Error resuming context at start: " + err.message + " " +Date.now();
        });
      }
    }

    const validation = this._validateAudioChain();
    if (!validation.valid) {
      console.warn('VOICE_PRECHECKS_ISSUE: Audio chain invalid at start:', validation.issues);
    }

    //If sample rate somehow goes to 0, recording will be messed up
    if(this._audioContext.sampleRate === 0){
      throw new Error("BAD SAMPLE RATE");
    }else{
      this._sampleRate = this._audioContext.sampleRate;
    }

    this._encoderWorker.postMessage({
      command: 'init',
      config: {
        sampleRate: this._audioContext.sampleRate,
        numChannels: this.numChannels,
        useTrim: this.encoderUseTrim,
        quietTrimThreshold: this.encoderQuietTrimThreshold,
        quietTrimSlackBack: this.encoderQuietTrimSlackBack,
      },
    });
  }

  /**
   * Stop recording
   */
  stop() {
    if (this._state !== 'recording') {
      //console.warn('recorder stop called out of state',Date.now(),this._state);
      return;
    }

    // In file mode, stop playing the audio file
    if (_audioFileMode) {
      this._stopFilePlayback();
    } else if (iPad) {
      // Reliability enhancement for iPad. Reconnect the source to the gain node.
      this._source.connect(this._gain);
    }

    if (this._recordingStartTime > this._quietStartTime) {
      this._isSilentRecording = true;
      this._silentRecordingConsecutiveCount += 1;
      this._eventTarget.dispatchEvent(new Event('silentrecording'));
    } else {
      this._isSilentRecording = false;
      this._silentRecordingConsecutiveCount = 0;
      this._eventTarget.dispatchEvent(new Event('unsilentrecording'));
    }

    this._state = 'inactive';
    this._recordingStartTime = 0;
    this._encoderWorker.postMessage({
      command: 'exportWav',
      type: 'audio/wav',
    });

    this._eventTarget.dispatchEvent(new Event('stop'));
  }

  cancelStream() {
    if (globalStream) {
      globalStream.getTracks().forEach(track => {
        track.stop();
      });
      globalStream = null;
    }
  }

  restartStream() {
    if (!globalStream && this._initialized) {
      this._initStream(true);
    }
  }

  _exportWav(evt) {
    let recordingProps = this._recordingSession;
    const event = new CustomEvent('dataavailable', {
      detail: {
        data: evt.data,
        samples16k: evt.samples16k || null,
        props:recordingProps,
        allZeros: evt.zeroBuffer,
        lowVolumeOrMuted: evt.lowVolumeOrMuted,
        ipadLowVolume: evt.ipadLowVolume,
        audioQuality: {
          intelligibilitySeverity: this._intelligibilitySeverity,
          clippingPercentage: (this._clip * 100) || 0, // Hard clipping (>0.99)
          nearClippingPercentage: (this._nearClip * 100) || 0, // Near-clipping (>=0.8, matches backend)
          rms: this._db, // RMS in dB
          zcrDuringClipping: this._zcrDuringClipping, // Severity formula input (TT-1110: was computed but never forwarded)
          spectralFlatness: this._spectralFlatness, // Severity + static-detection input
          spectralRolloff: this._spectralRolloff, // Static-detection input
          highLowFreqRatio: this._highLowFreqRatio, // Static-detection input
          // Not gated/decayed the way zcrDuringClipping is -- "always available" per the
          // volume getter's own comment. Plausible signal for "mic alive but attenuated"
          // distinct from anything else logged (TT-1110).
          zcr: this._zcr,
          peakAmplitude: this._peakAmplitude,
          crestFactorDB: this._crestFactorDB,
        },
        staticResult: {
          detected: this._staticDetected,
          type: this._staticType, // 'low-band' | 'broadband' | null
        },
        // spectralFlatness/spectralRolloff/highLowFreqRatio refresh unconditionally on the
        // AudioWorklet path but only if(this._analyser) on the ScriptProcessor path -- without
        // this flag, pooling both paths' data treats two different feature definitions as one.
        usingAudioWorklet: this._usingAudioWorklet,
      }
    });
    this._eventTarget.dispatchEvent(event);
    this._encoderWorker.postMessage({ command: 'clear' });
  }

  /**
   * Posts channel data to the WAV encoder worker.
   * Accepts an array of Float32Array channel buffers (used by both
   * AudioWorklet and ScriptProcessor paths).
   * @param {Float32Array[]} channelBuffers
   */
  _postBuffersToEncoder(channelBuffers) {
    if (this._state !== 'recording') {
      console.warn('recorder _postBuffersToEncoder called out of state');
      return;
    }
    this._encoderWorker.postMessage({
      command: 'record',
      buffer: channelBuffers,
    });
  }

  /**
   * Transfers AudioWorklet-owned channel buffers to the WAV encoder worker.
   * Call only after main-thread reads are complete because transfer detaches
   * the underlying ArrayBuffers.
   * @param {Float32Array[]} channelBuffers
   */
  _transferBuffersToEncoder(channelBuffers) {
    if (this._state !== 'recording') {
      console.warn('recorder _transferBuffersToEncoder called out of state');
      return;
    }
    this._encoderWorker.postMessage({
      command: 'record',
      buffer: channelBuffers,
    }, channelBuffers.map(channel => channel.buffer));
  }

  /**
   * Legacy adapter that extracts channel arrays from an AudioBuffer
   * and forwards them to the encoder.
   * @param {AudioBuffer} inputBuffer
   */
  _recordBuffers(inputBuffer) {
    const buffer = [];
    for (let i = 0; i < inputBuffer.numberOfChannels; i++) {
      buffer[i] = inputBuffer.getChannelData(i);
    }
    this._postBuffersToEncoder(buffer);
  }

  _setIsMicMuted(evt) {
    let eventType = evt && evt.type;
    if (eventType === 'unmute') {
      clearTimeout(this.mutedTroubleshootTimeout);
      this.mutedTroubleshootTimeout = null;
    }
    console.log("MUTE CHANGED",eventType);
    logSessionInfo("mute_changed_"+eventType,{eventType});

    if (!this.useAutoMuteDetect) {
      return;
    }
    // TODO incorporate _maxVolume
    if (this._instant >= this.muteThreshold) {
      if (this._isMicMuted) {
        this._isMicMuted = false;
        this._eventTarget.dispatchEvent(new Event('unmute'));
      }
      return;
    }

    if (!this._isMicMuted && (this._slow < this.muteThreshold)) {
      this._isMicMuted = true;
      this._eventTarget.dispatchEvent(new Event('mute'));
      console.info(
        'mute - instant: %s - slow: %s - track muted: %s',
        this._instant, this._slow, this._tracks && this._tracks[0] ? this._tracks[0].muted : 'unknown',
      );

      if (this._state === 'recording') {
        this.stop();
        console.info('stopped recording on _setIsMicMuted');
      }
    }
  }

  _setIsMicQuiet() {
    const now = this._audioContext.currentTime;

    const isMicQuiet = (this._maxVolume < this.volumeThreshold ||
      this._slow < this.quietThreshold);

    // start record the time when the line goes quiet
    // fire event
    if (!this._isMicQuiet && isMicQuiet) {
      this._quietStartTime = this._audioContext.currentTime;
      this._eventTarget.dispatchEvent(new Event('quiet'));
    }
    // reset quiet timer when there's enough sound
    if (this._isMicQuiet && !isMicQuiet) {
      this._quietStartTime = 0;
      this._eventTarget.dispatchEvent(new Event('unquiet'));
    }
    this._isMicQuiet = isMicQuiet;

    // if autoincrease is enabled, exponentially increase the mimimun recording
    // time based on consecutive silent recordings
    const recordingTimeMin =
      /*(this.recordingTimeMinAutoIncrease) ?
        (this.recordingTimeMin - 1) +
        ((this.recordingTimeMax) **
         (1 - (1 / (this._silentRecordingConsecutiveCount + 1)))) :*/
        this.recordingTimeMin;


    // detect voice pause and stop recording
    if (this.autoStopRecording &&
      this._isMicQuiet && this._state === 'recording' &&
      // have I been recording longer than the minimum recording time?
      now - this._recordingStartTime > recordingTimeMin &&
      // has the slow sample value been below the quiet threshold longer than
      // the minimum allowed quiet time?
      now - this._quietStartTime > this.quietTimeMin
    ) {
      this.stop();
    }
  }

  /**
   * Initializes the AudioContext
   * Aassigs it to this._audioContext. Adds visibitily change event listener
   * to suspend the audio context when the browser tab is hidden.
   * @return {Promise} resolution of AudioContext
   */
  _initAudioContext() {
    this._audioContext = globalContext;
    return Promise.resolve();
  }

  /**
   * Tears down the current microphone volume processor before replacement.
   * AudioWorklet processors receive an explicit stop message so their
   * process loop exits instead of continuing from a stale graph node.
   */
  _teardownMicVolumeProcessor() {
    if (!this._micVolumeProcessor) {
      return;
    }

    if (this._gain && this._connectedMicVolumeProcessor === this._micVolumeProcessor) {
      try {
        this._gain.disconnect(this._micVolumeProcessor);
      } catch (err) {
        console.warn('Unable to disconnect gain from microphone volume processor:', err.message);
      }
      this._connectedMicVolumeProcessor = null;
    }

    if (this._micVolumeProcessor.port) {
      this._micVolumeProcessor.port.postMessage({ type: 'stop' });
      this._micVolumeProcessor.port.onmessage = null;
    }

    if (this._micVolumeProcessor.onaudioprocess) {
      this._micVolumeProcessor.onaudioprocess = null;
    }

    try {
      this._micVolumeProcessor.disconnect();
    } catch (err) {
      console.warn('Unable to disconnect microphone volume processor:', err.message);
    }

    this._micVolumeProcessor = null;
  }

  /**
   * Connects the persistent gain node to the active microphone processor.
   * Re-init can replace the processor while keeping the gain node, so this
   * connection must be refreshed outside the one-time gain setup.
   */
  _connectMicVolumeProcessor() {
    if (!this._gain || !this._micVolumeProcessor) {
      return;
    }

    if (this._connectedMicVolumeProcessor === this._micVolumeProcessor) {
      return;
    }

    if (this._connectedMicVolumeProcessor) {
      try {
        this._gain.disconnect(this._connectedMicVolumeProcessor);
      } catch (err) {
        console.warn('Unable to disconnect previous microphone volume processor:', err.message);
      }
    }

    this._gain.connect(this._micVolumeProcessor);
    // AudioWorkletNode needs a destination connection to keep the graph alive;
    // it outputs silence so no audible mic feedback occurs.
    this._micVolumeProcessor.connect(this._audioContext.destination);
    this._connectedMicVolumeProcessor = this._micVolumeProcessor;
  }

  /**
   * Loads the AudioWorklet module once per recorder instance.
   * Clears failed loads so transient fetch/module failures can retry and still
   * fall back to ScriptProcessor for the current initialization attempt.
   * @returns {Promise<void>}
   */
  _loadAudioWorkletModule() {
    if (!this._workletModuleReady) {
      this._workletModuleReady = this._audioContext.audioWorklet
        .addModule(audioProcessorWorkletUrl)
        .catch((err) => {
          this._workletModuleReady = null;
          throw err;
        });
    }

    return this._workletModuleReady;
  }

  /**
   * Private initializer of the audio buffer processor.
   * Attempts to use AudioWorklet (off-main-thread processing) when the
   * ENABLE_AUDIO_WORKLET feature flag is on and the browser supports it.
   * Falls back to the legacy ScriptProcessorNode otherwise.
   *
   * @returns {Promise<void>}
   */
  async _initMicVolumeProcessor() {
    const useAudioWorklet = FEATURE_FLAGS.ENABLE_AUDIO_WORKLET;
    if (useAudioWorklet) {
      this._teardownMicVolumeProcessor();
    }

    if (useAudioWorklet && this._audioContext.audioWorklet) {
      try {
        await this._loadAudioWorkletModule();
        this._micVolumeProcessor = this._createAudioWorkletProcessor();
        this._usingAudioWorklet = true;
        console.info('AudioWorklet processor initialized successfully');
        return;
      } catch (err) {
        console.warn('AudioWorklet initialization failed, falling back to ScriptProcessor:', err.message);
        logSentryException(err, 'AudioWorklet addModule/construction failed', { originalError: err?.message, originalStack: err?.stack });
      }
    }

    this._usingAudioWorklet = false;
    this._micVolumeProcessor = this._createScriptProcessorFallback();
  }

  /**
   * Creates an AudioWorkletNode and wires up the port.onmessage handler.
   * The worklet computes PCM metrics and FFT-based spectral metrics on the
   * audio thread; the main thread reads pre-computed values from data.metrics.
   * @returns {AudioWorkletNode}
   */
  _createAudioWorkletProcessor() {
    const workletNode = new AudioWorkletNode(this._audioContext, 'audio-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [this.numChannels],
      processorOptions: {
        bufferLength: this._bufferLength,
        numChannels: this.numChannels,
        sampleRate: this._audioContext.sampleRate,
      },
    });

    let isListening = false;

    workletNode.port.onmessage = (event) => {
      const { data } = event;
      if (!data || data.type !== 'audioData') return;
      if (!data.channels || !data.channels[0]) return;

      if (this._audioContext.state !== 'running') {
        console.warn('VOICE_PRECHECKS_ISSUE: Audio context not running during processing:', this._audioContext.state);
        this._audioContext.resume().catch(err => console.error('Failed to resume:', err));
      }

      if (this._state === 'recording') {
        const streamHealth = this._isStreamHealthy();
        if (!isListening) {
          this.sentryMicErrorLogs.listeningStarted.push(Date.now());
          this.sentryMicErrorLogs.trackErrorsOnStart.push(streamHealth.healthy ? 'Healthy' : 'Unhealthy: ' + streamHealth.reason);
          isListening = true;
        }
        if (!streamHealth.healthy) {
          console.warn('VOICE_PRECHECKS_ISSUE: Stream is not healthy:', streamHealth.reason);
        }
        if (this._tracks && this._tracks[0] && !this._tracks[0].enabled) {
          console.warn('VOICE_PRECHECKS_ISSUE: Track not enabled on audio process:', this._tracks[0]?.enabled);
        }

        const { metrics } = data;

        this._instant = metrics.instant;
        this._db = metrics.db;
        this._slow = (0.85 * this._slow) + (0.15 * this._db);
        this._soft = this._slow <= this.softThresholdDB;
        this._loud = this._slow >= this.loudThresholdDB;
        this._clip = metrics.clip;
        this._nearClip = metrics.nearClip;
        this._maxVolume = this._db > this._maxVolume ? this._db : this._maxVolume;
        this._minVolume = this._db < this._minVolume ? this._db : this._minVolume;
        this._maxClip = this._clip > this._maxClip ? this._clip : this._maxClip;

        this._peakAmplitude = metrics.peakAmplitude;
        this._crestFactorDB = metrics.crestFactorDB;
        this._zcr = metrics.zcr;

        if (this._nearClip > 0.05) {
          this._zcrDuringClipping = metrics.zcrDuringClipping;
          this._spectralFlatness = metrics.spectralFlatness;
          this._intelligibilitySeverity = this._calculateIntelligibilitySeverity();
        } else {
          this._zcrDuringClipping *= 0.95;
          this._intelligibilitySeverity *= 0.95;

          if (this._intelligibilitySeverity < 1.0) {
            this._zcrDuringClipping = 0;
            this._intelligibilitySeverity = 0;
          }
        }

        // Spectral metrics are pre-computed by the worklet's FFT.
        // Assigned after the near-clip decay block so these always reflect the
        // current FFT -- matching original behavior where the analyser block
        // overwrote any decayed values.
        this._spectralFlatness = metrics.spectralFlatness;
        this._spectralRolloff = metrics.spectralRolloff;
        this._highLowFreqRatio = metrics.highLowFreqRatio;
        this._staticDetected = metrics.staticDetected;
        this._staticType = metrics.staticType;

        if (!this._recordingSession.volumeOnly) {
          this._transferBuffersToEncoder(data.channels);
        }

        if ((this._audioContext.currentTime - this._recordingStartTime) > this.recordingTimeMax) {
          this.stop();
        }
      } else {
        if (isListening) {
          this.sentryMicErrorLogs.listeningStopped.push(Date.now());
          const streamHealth = this._isStreamHealthy();
          this.sentryMicErrorLogs.trackErrorsOnStop.push(streamHealth.healthy ? 'Healthy' : 'Unhealthy: ' + streamHealth.reason);
          isListening = false;
        }
      }
    };

    return workletNode;
  }

  /**
   * Creates the legacy ScriptProcessorNode fallback.
   * Preserves the original onaudioprocess behavior exactly.
   * @returns {ScriptProcessorNode}
   */
  _createScriptProcessorFallback() {
    /* eslint no-plusplus: ["error", { "allowForLoopAfterthoughts": true }] */
    const processor = this._audioContext.createScriptProcessor(
      this._bufferLength,
      this.numChannels,
      this.numChannels
    );

    let isListening = false;

    processor.onaudioprocess = (evt) => {
      if (this._audioContext.state !== 'running') {
        console.warn('VOICE_PRECHECKS_ISSUE: Audio context not running during processing:', this._audioContext.state);
        this._audioContext.resume().catch(err => console.error('Failed to resume:', err));
      }

      if (this._state === 'recording') {
        const streamHealth = this._isStreamHealthy();
        if (!isListening) {
          this.sentryMicErrorLogs.listeningStarted.push(Date.now());
          this.sentryMicErrorLogs.trackErrorsOnStart.push(streamHealth.healthy ? 'Healthy' : 'Unhealthy: ' + streamHealth.reason);
          isListening = true;
        }
        if (!streamHealth.healthy) {
          console.warn('VOICE_PRECHECKS_ISSUE: Stream is not healthy:', streamHealth.reason);
        }
        if (this._tracks && this._tracks[0] && !this._tracks[0].enabled) {
          console.warn('VOICE_PRECHECKS_ISSUE: Track not enabled on audio process:', this._tracks[0]?.enabled);
        }
        if (!this._recordingSession.volumeOnly) {
          this._recordBuffers(evt.inputBuffer);
        }

        if ((this._audioContext.currentTime - this._recordingStartTime) > this.recordingTimeMax) {
          this.stop();
        }

        const input = evt.inputBuffer.getChannelData(0);
        let sum = 0.0;
        let clipCount = 0;
        let nearClipCount = 0;

        for (let i = 0; i < input.length; ++i) {
          sum += input[i] * input[i];
          if (Math.abs(input[i]) > 0.99) {
            clipCount += 1;
          }
          if (Math.abs(input[i]) >= 0.8) {
            nearClipCount += 1;
          }
        }

        this._instant = Math.sqrt(sum / input.length);
        this._db = 20 * Math.log10(this._instant);
        this._slow = (0.85 * this._slow) + (0.15 * this._db);
        this._soft = this._slow <= this.softThresholdDB;
        this._loud = this._slow >= this.loudThresholdDB;
        this._clip = (input.length) ? clipCount / input.length : 0;
        this._nearClip = (input.length) ? nearClipCount / input.length : 0;
        this._maxVolume = this._db > this._maxVolume ? this._db : this._maxVolume;
        this._minVolume = this._db < this._minVolume ? this._db : this._minVolume;
        this._maxClip = this._clip > this._maxClip ? this._clip : this._maxClip;

        this._peakAmplitude = this._calculatePeakAmplitude(input);
        this._crestFactorDB = this._calculateCrestFactor(this._peakAmplitude, this._instant);
        this._zcr = this._calculateZCR(input);

        if (this._nearClip > 0.05) {
          this._zcrDuringClipping = this._calculateZCRDuringClipping(input);
          this._spectralFlatness = this._calculateSpectralFlatness(input);
          this._intelligibilitySeverity = this._calculateIntelligibilitySeverity();
        } else {
          this._zcrDuringClipping *= 0.95;
          this._spectralFlatness *= 0.95;
          this._intelligibilitySeverity *= 0.95;

          if (this._intelligibilitySeverity < 1.0) {
            this._zcrDuringClipping = 0;
            this._spectralFlatness = 0;
            this._intelligibilitySeverity = 0;
          }
        }

        if (this._analyser) {
          this._spectralRolloff = this._calculateSpectralRolloff(0.85);
          this._highLowFreqRatio = this._calculateHighLowFrequencyRatio(2000);
          this._spectralFlatness = this._calculateSpectralFlatness(input);

          const staticResult = this._detectStatic();
          this._staticDetected = staticResult.detected;
          this._staticType = staticResult.type;
        }
      } else {
        if (isListening) {
          this.sentryMicErrorLogs.listeningStopped.push(Date.now());
          const streamHealth = this._isStreamHealthy();
          this.sentryMicErrorLogs.trackErrorsOnStop.push(streamHealth.healthy ? 'Healthy' : 'Unhealthy: ' + streamHealth.reason);
          isListening = false;
        }
      }
    };

    return processor;
  }

  /**
   * Checks if the stream is healthy
   * @returns an object with a healthy property and a reason property
   */
  _isStreamHealthy() {
    if (!this._tracks || !this._tracks[0]) {
      return { healthy: false, reason: 'No tracks available' };
    }
    
    const track = this._tracks[0];
    const settings = track.getSettings();
    
    if (track.muted) {
      return { healthy: false, reason: 'Track is muted' };
    }
    
    if (!track.enabled) {
      return { healthy: false, reason: 'Track is disabled' };
    }
    
    if (track.readyState !== 'live') {
      return { healthy: false, reason: `Track state is ${track.readyState}` };
    }
    
    if (settings.volume !== undefined && settings.volume < 0.1) {
      return { healthy: false, reason: `Volume too low: ${settings.volume}` };
    }

    if (this._audioContext.state !== 'running') {
      return { healthy: false, reason: 'Audio context not running: ' + this._audioContext.state };
    }
    
    return { healthy: true };
  }

  /**
   * Validates the audio chain
   * @returns an object with a valid property and an issues property
   */
  _validateAudioChain() {
    const issues = [];
    
    // Check source
    if (!this._source) {
      issues.push('❌ No audio source');
    } else if (this._source.numberOfOutputs === 0) {
      issues.push('⚠️ Source has no output connections');
    }
    
    // Check gain
    if (!this._gain) {
      issues.push('❌ No gain node');
    } else {
      if (this._gain.gain.value < 0.1) {
        issues.push(`⚠️ Gain value very low: ${this._gain.gain.value}`);
      }
      if (this._gain.numberOfOutputs === 0) {
        issues.push('⚠️ Gain has no output connections');
      }
    }
    
    // Check processor
    if (!this._micVolumeProcessor) {
      issues.push('❌ No volume processor');
    } else if (this._micVolumeProcessor.numberOfInputs === 0) {
      issues.push('⚠️ Processor has no input connections');
    }
    
    // Check tracks
    if (!this._tracks || !this._tracks[0]) {
      issues.push('❌ No audio tracks');
    } else {
      const track = this._tracks[0];
      if (!track.enabled) issues.push('❌ Track disabled');
      if (track.muted) issues.push('❌ Track muted');
      if (track.readyState !== 'live') issues.push(`❌ Track not live: ${track.readyState}`);
    }
    
    // Check context
    if (this._audioContext.state !== 'running') {
      issues.push(`❌ Audio context not running: ${this._audioContext.state}`);
    }
    
    if (issues.length === 0) {
      return { valid: true, issues: [] };
    } else {
      return { valid: false, issues };
    }
  }

  initAudioFromStream(stream){
    this._tracks = stream.getAudioTracks();
    // assumes single channel
    if (this._tracks && this._tracks[0]) {
      this._tracks[0].onmute = (evt) => this._setIsMicMuted(evt);
      this._tracks[0].onunmute = (evt) => this._setIsMicMuted(evt);
    }

    if (FEATURE_FLAGS.ENABLE_AUDIO_WORKLET && this._source) {
      try {
        this._source.disconnect();
      } catch (err) {
        console.warn('Unable to disconnect previous audio source:', err.message);
      }
    }

    this._source = this._audioContext.createMediaStreamSource(stream);

    // Initialize gain once per lifecycle
    if(!this._gain){
      this._gain = this._audioContext.createGain();
      // Apply gain boost only on iPad to compensate for disabled auto gain control
      // When ENABLE_AUDIO_PROCESSING_CONSTRAINTS is enabled (and not disabled via URL param),
      // autoGainControl is on, so no manual gain boost is needed
      if (iPad && !isAudioProcessingEnabled() && isIPadBelowVersion(18)) {
        this._gain.gain.value = 5.0;
      }

      if (!FEATURE_FLAGS.ENABLE_AUDIO_WORKLET) {
        this._gain.connect(this._micVolumeProcessor);
      }
    }

    // AnalyserNode is only needed for the ScriptProcessor fallback path.
    // Evaluated on every init (not just first) so a re-init that falls back
    // from AudioWorklet to ScriptProcessor still gets an AnalyserNode.
    if (!this._usingAudioWorklet && !this._analyser) {
      this._analyser = this._audioContext.createAnalyser();
      this._analyser.fftSize = 512;
      this._analyser.smoothingTimeConstant = 0;
      this._frequencyData = new Float32Array(this._analyser.frequencyBinCount);
      console.info('AnalyserNode enabled for ScriptProcessor fallback, FFT size:', this._analyser.fftSize);
      this._gain.connect(this._analyser);
    }

    if (FEATURE_FLAGS.ENABLE_AUDIO_WORKLET) {
      this._connectMicVolumeProcessor();
    } else if(!this._stream){
      this._gain.connect(this._micVolumeProcessor);
      this._micVolumeProcessor.connect(this._audioContext.destination);
    }

    this._source.connect(this._gain);
    this._stream = stream;
  }

  /*
   * Private initializers
   */

  /**
   * Load audio from a local file and create an AudioBuffer
   * @param {string} filePath - Path to the audio file
   * @return {Promise<AudioBuffer>} Promise that resolves with the AudioBuffer
   */
  async _loadAudioFile(filePath) {
    try {
      console.info('Loading audio file:', filePath);
      const response = await fetch(filePath);
      if (!response.ok) {
        throw new Error(`Failed to load audio file: ${response.status} ${response.statusText}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this._audioContext.decodeAudioData(arrayBuffer);
      console.info('Audio file loaded successfully. Duration:', audioBuffer.duration, 'seconds');
      return audioBuffer;
    } catch (err) {
      console.error('Error loading audio file:', err);
      throw err;
    }
  }

  /**
   * Initialize stream from audio file
   * Creates a buffer source that simulates microphone input
   * Playback will start when start() is called, not during initialization
   * @return {Promise} Promise that resolves when the audio file stream is ready
   */
  async _initStreamFromFile() {
    try {
      console.info('Initializing stream from audio file:', AUDIO_FILE_PATH);
      
      // Load the audio file into a buffer
      _audioFileBuffer = await this._loadAudioFile(AUDIO_FILE_PATH);
      _audioFileMode = true;
      _audioFilePlaybackPosition = 0; // Start from beginning
      _audioFilePlaybackStartTime = 0;
      
      // Create a MediaStreamDestination to simulate microphone stream
      const streamDestination = this._audioContext.createMediaStreamDestination();
      
      // Store reference for later use
      this._fileStreamDestination = streamDestination;
      
      // Store duration for later use
      this._audioFileDuration = _audioFileBuffer.duration;
      
      // Set the global stream
      globalStream = streamDestination.stream;
      
      console.info('Audio file stream initialized successfully');
      this._startStream();
      
      return Promise.resolve();
    } catch (err) {
      console.error('Failed to initialize stream from audio file:', err);
      console.info('Falling back to microphone input');
      _audioFileMode = false;
      _audioFileBuffer = null;
      // Fall back to microphone
      return this._initStreamFromMicrophone();
    }
  }

  /**
   * Start playback from the audio file buffer
   * This is called when recording starts in file mode
   * Creates a new BufferSource (they're single-use) and starts from the saved position
   */
  _startFilePlayback() {
    if (!_audioFileMode || !_audioFileBuffer) {
      console.warn('File playback requested but not in file mode');
      return;
    }
    
    // Clean up any existing buffer source
    if (this._fileBufferSource) {
      try {
        this._fileBufferSource.onended = null;
        this._fileBufferSource.disconnect();
      } catch (e) {
        // Buffer source might already be stopped/disconnected
      }
    }
    
    // Create a new BufferSource (they're single-use)
    this._fileBufferSource = this._audioContext.createBufferSource();
    this._fileBufferSource.buffer = _audioFileBuffer;
    this._fileBufferSource.loop = false;
    
    // Connect to the stream destination
    this._fileBufferSource.connect(this._fileStreamDestination);
    
    // Set up the ended handler
    this._fileBufferSource.onended = () => {
      console.info('Audio file playback ended naturally after', this._audioFileDuration, 'seconds');
      
      // Dispatch custom event that application can listen to
      this._eventTarget.dispatchEvent(new CustomEvent('audiofileended', {
        detail: {
          duration: this._audioFileDuration,
          timestamp: Date.now()
        }
      }));
      
      // Auto-stop recording when file ends
      if (this._state === 'recording') {
        console.info('Auto-stopping recording - audio file ended');
        this.stop();
      }
    };
    
    // Start playback from current position
    const remainingDuration = this._audioFileDuration - _audioFilePlaybackPosition;
    console.info('Starting audio file playback from position', _audioFilePlaybackPosition.toFixed(2), 
                 'seconds (', remainingDuration.toFixed(2), 'seconds remaining)');
    
    this._fileBufferSource.start(0, _audioFilePlaybackPosition);
    _audioFilePlaybackStartTime = this._audioContext.currentTime;
    this._audioFilePlaybackPaused = false;
  }

  /**
   * Stop playback from the audio file buffer
   * Calculates elapsed time and updates position for resume
   * This is called during normal recording stop (pause)
   */
  _stopFilePlayback() {
    if (!this._fileBufferSource || this._audioFilePlaybackPaused) {
      return;
    }
    
    // Calculate how much time has elapsed since we started this playback segment
    const elapsedTime = this._audioContext.currentTime - _audioFilePlaybackStartTime;
    
    // Update the playback position for resume
    _audioFilePlaybackPosition += elapsedTime;
    
    // Clamp to file duration
    if (_audioFilePlaybackPosition > this._audioFileDuration) {
      _audioFilePlaybackPosition = this._audioFileDuration;
    }
    
    // Stop the buffer source
    try {
      this._fileBufferSource.onended = null; // Remove handler to prevent auto-stop
      this._fileBufferSource.stop();
      this._fileBufferSource.disconnect();
    } catch (e) {
      console.warn('Error stopping buffer source:', e);
    }
    
    this._fileBufferSource = null;
    this._audioFilePlaybackPaused = true;
  }

  /**
   * Initialize stream from microphone using getUserMedia
   * @return {Promise} Promise that resolves when the microphone stream is ready
   */
  _initStreamFromMicrophone(fromRetry) {
    this.isStreamInitInProgress = true;
    try {
      return navigator.mediaDevices.getUserMedia(getConstraints(iPad))
        .then((stream) => {
          globalStream = stream;
          this._startStream();
          if (fromRetry) {
            this.sentryMicErrorLogs.reinit_error_msg = "NO_ERROR "+Date.now();
          }
        })
        .catch((err)=>{
          console.error("Recorder could not get microphone",err.name,err.message);
          if (fromRetry) {
            this.sentryMicErrorLogs.reinit_error_msg = "Error initializing stream: " + err.message + " " +Date.now();
          }
          throw(err)
        }).finally(() => {
          this.isStreamInitInProgress = false;
        });
    } catch (err) {
      console.error("Recorder could not get microphone in outer try catch: ",err.name,err.message);
      if (fromRetry) {
        this.sentryMicErrorLogs.reinit_error_msg = "Error initializing stream in outer try catch: " + err.message + " " +Date.now();
      }
      throw(err);
    }
  }

  /**
   * Sets microphone or audio file as input source
   * @return {Promise} returns a promise that resolves when the audio input
   *   has been connected
   */
  _initStream(fromRetry) {
    // Check if audio file path is configured
    if (AUDIO_FILE_PATH && AUDIO_FILE_PATH.trim() !== '' && window.location.host.startsWith('localhost')) {
      console.info('Audio file path configured:', AUDIO_FILE_PATH);
      return this._initStreamFromFile();
    } else {
      console.info('No audio file configured, using microphone');
      return this._initStreamFromMicrophone(fromRetry);
    }
  }

  _startStream() {
    if(globalStream){
      this.initAudioFromStream(globalStream);
      if (this._tracks && this._tracks[0]) {
        console.info('using media stream track labeled: ', this._tracks[0].label);
        console.info("media stream attributes:",JSON.stringify(this._tracks[0].getSettings()));
        
        // Check if track is muted on stream initialization
        if (this._tracks[0].muted) {
          console.warn('VOICE_PRECHECKS_ISSUE: Track is muted on stream initialization');
          this._eventTarget.dispatchEvent(new Event('trackmuted'));
        }
      }
      this._eventTarget.dispatchEvent(new Event('streamReady'));
    }else{
      console.log("Global Microphone Stream not Initialized");
      throw new Error("Global Microphone Stream not Initialized");
    }
  }
  /**
   * Calculate Zero-Crossing Rate during near-clipping
   * Measures if sustained tones (vowels, low ZCR) or transient sounds (consonants, high ZCR) are near-clipping
   * Uses 0.8 threshold to match backend behavior
   * 
   * @param {Float32Array} input - Audio buffer
   * @return {number} ZCR value (0-1 range, typically 0.05-0.3)
   */
  _calculateZCRDuringClipping(input) {
    const threshold = 0.8; // Near-clipping threshold (matches backend)
    let zeroCrossings = 0;
    let nearClippedSampleCount = 0;
    
    for (let i = 1; i < input.length; i++) {
      // Only count zero crossings near near-clipped samples
      if (Math.abs(input[i]) >= threshold || Math.abs(input[i-1]) >= threshold) {
        nearClippedSampleCount++;
        // Zero crossing detected
        if ((input[i] >= 0 && input[i-1] < 0) || (input[i] < 0 && input[i-1] >= 0)) {
          zeroCrossings++;
        }
      }
    }
    
    return nearClippedSampleCount > 0 ? zeroCrossings / nearClippedSampleCount : 0;
  }

  /**
   * Calculate general Zero-Crossing Rate
   * Measures the rate at which the signal changes sign
   * Higher ZCR indicates more high-frequency content or noise
   * 
   * @param {Float32Array} input - Audio buffer
   * @return {number} ZCR value (0-1 range, typically 0.05-0.3)
   */
  _calculateZCR(input) {
    let zeroCrossings = 0;
    
    for (let i = 1; i < input.length; i++) {
      // Zero crossing detected
      if ((input[i] >= 0 && input[i-1] < 0) || (input[i] < 0 && input[i-1] >= 0)) {
        zeroCrossings++;
      }
    }
    // Normalize by buffer length
    return input.length > 1 ? zeroCrossings / (input.length - 1) : 0;
  }

  /**
   * Calculate Spectral Flatness (Wiener entropy)
   * Measures if tonal (speech harmonics, low flatness) or noisy (broadband, high flatness) content is clipping
   * 
   * @param {Float32Array} input - Audio buffer
   * @return {number} Spectral flatness (0-1 range, typically 0.01-0.15)
   */
  _calculateSpectralFlatness(input) {
    // Use AnalyserNode FFT if available (matches backend's FFT implementation)
    if (this._analyser && this._frequencyData) {
      // Get frequency data from AnalyserNode
      this._analyser.getFloatFrequencyData(this._frequencyData);
      
      const binCount = this._frequencyData.length;
      let geometricMean = 0; // Will use log domain for geometric mean
      let arithmeticMean = 0;
      let validBins = 0;
      
      for (let i = 0; i < binCount; i++) {
        // Convert from dB to linear power
        const power = Math.pow(10, this._frequencyData[i] / 10);
        
        if (power > 1e-10) { // Ignore very small values
          // For geometric mean in log domain: log(GM) = (1/N) * sum(log(x))
          geometricMean += Math.log(power);
          arithmeticMean += power;
          validBins++;
        }
      }
      
      if (validBins === 0 || arithmeticMean === 0) return 0;
      
      // Complete geometric mean calculation
      geometricMean = Math.exp(geometricMean / validBins);
      arithmeticMean /= validBins;
      
      // Spectral flatness = geometric mean / arithmetic mean
      // Range: 0 (tonal) to 1 (noise-like)
      const flatness = geometricMean / arithmeticMean;
      
      return Math.max(0, Math.min(1, flatness));
    }
    
    // Fallback: Simple time-domain approximation if AnalyserNode unavailable
    // This is less accurate but better than nothing
    const bands = 8;
    const bandSize = Math.floor(input.length / bands);
    const bandEnergies = new Array(bands).fill(0);
    
    for (let b = 0; b < bands; b++) {
      const start = b * bandSize;
      const end = Math.min(start + bandSize, input.length);
      
      for (let i = start; i < end; i++) {
        bandEnergies[b] += input[i] * input[i];
      }
      bandEnergies[b] = bandEnergies[b] / (end - start);
    }
    
    // Calculate geometric and arithmetic means
    let geometricMean = 0;
    let arithmeticMean = 0;
    let validBands = 0;
    
    for (let b = 0; b < bands; b++) {
      if (bandEnergies[b] > 1e-10) {
        geometricMean += Math.log(bandEnergies[b]);
        arithmeticMean += bandEnergies[b];
        validBands++;
      }
    }
    
    if (validBands === 0 || arithmeticMean === 0) return 0;
    
    geometricMean = Math.exp(geometricMean / validBands);
    arithmeticMean /= validBands;
    
    const flatness = geometricMean / arithmeticMean;
    
    return Math.max(0, Math.min(1, flatness));
  }

  /**
   * Calculate Intelligibility Severity Score
   * Combines near-clipping percentage, ZCR, and spectral flatness to predict speech intelligibility
   * Uses near-clipping threshold (0.8) to match backend behavior
   * 
   * Formula from batch analysis, adapted for real-time streaming
   * 
   * @return {number} Severity score (0-100):
   *   0-30: Intelligible
   *   30-40: Moderate
   *   40-50: Severe
   *   50+: Critical/Unintelligible
   */
  _calculateIntelligibilitySeverity() {
    // Use near-clipping percentage (0.8 threshold) to match backend
    const nearClippingPct = this._nearClip * 100;
    const zcr = this._zcrDuringClipping;
    const flatness = this._spectralFlatness;
    
    // Formula weights (matches backend):
    // - 30% near-clipping amount (detects compression/limiting)
    // - 35% ZCR (type of clipping: vowels vs consonants)
    // - 35% spectral flatness (tonal vs noisy)
    
    const severity = (
      0.30 * (nearClippingPct / 100) +
      0.35 * (1 - Math.min(zcr / 0.15, 1.0)) +
      0.35 * (1 - Math.min(flatness / 0.06, 1.0))
    ) * 100;
    
    return Math.max(0, Math.min(100, severity));
  }

  /**
   * Calculate Peak Amplitude
   * Maximum absolute amplitude in the buffer
   * 
   * @param {Float32Array} input - Audio buffer
   * @return {number} Peak amplitude (0-1 range)
   */
  _calculatePeakAmplitude(input) {
    let peak = 0;
    for (let i = 0; i < input.length; i++) {
      const abs = Math.abs(input[i]);
      if (abs > peak) {
        peak = abs;
      }
    }
    return peak;
  }

  /**
   * Calculate Crest Factor
   * Ratio of peak amplitude to RMS in dB
   * High values indicate spiky/transient sounds, low values indicate compressed/sustained sounds
   * 
   * @param {number} peak - Peak amplitude
   * @param {number} rms - RMS value
   * @return {number} Crest factor in dB (typically 6-20 dB for speech)
   */
  _calculateCrestFactor(peak, rms) {
    if (rms === 0 || peak === 0) return 0;
    return 20 * Math.log10(peak / rms);
  }

  /**
   * Calculate Spectral Rolloff using AnalyserNode FFT
   * Frequency below which X% of spectral energy is contained
   * Used for static detection
   * 
   * @param {number} rolloffPercent - Percentage threshold (default 0.85 = 85%)
   * @return {number} Rolloff frequency in Hz (typically 3000-6000 Hz for normal speech)
   */
  _calculateSpectralRolloff(rolloffPercent = 0.85) {
    if (!this._analyser || !this._frequencyData) return 0;
    
    // Get frequency data from AnalyserNode
    this._analyser.getFloatFrequencyData(this._frequencyData);
    
    const sampleRate = this._audioContext.sampleRate;
    const nyquist = sampleRate / 2;
    const binCount = this._frequencyData.length;
    const freqPerBin = nyquist / binCount;
    
    // Convert dB to power spectrum (matches backend)
    const powerSpectrum = new Float32Array(binCount);
    let totalEnergy = 0;
    
    for (let i = 0; i < binCount; i++) {
      // Convert from dB to power: 10^(dB/10)
      // Backend uses power spectrum from spectrogram, so we use /10 not /20
      powerSpectrum[i] = Math.pow(10, this._frequencyData[i] / 10);
      totalEnergy += powerSpectrum[i];
    }
    
    if (totalEnergy === 0) return 0;
    
    // Find frequency where cumulative energy exceeds threshold
    const threshold = totalEnergy * rolloffPercent;
    let cumulativeEnergy = 0;
    
    for (let i = 0; i < binCount; i++) {
      cumulativeEnergy += powerSpectrum[i];
      if (cumulativeEnergy >= threshold) {
        return i * freqPerBin;
      }
    }
    
    return nyquist; // Return Nyquist if threshold not reached
  }

  /**
   * Calculate High/Low Frequency Ratio
   * Ratio of energy in high frequencies (above cutoff) to low frequencies (below cutoff)
   * Used for static detection
   * 
   * @param {number} cutoffFreq - Cutoff frequency in Hz (default 2000 Hz)
   * @return {number} Ratio (typically 0.5-2.0 for normal speech)
   */
  _calculateHighLowFrequencyRatio(cutoffFreq = 2000) {
    if (!this._analyser || !this._frequencyData) return 0;
    
    // Get frequency data from AnalyserNode
    this._analyser.getFloatFrequencyData(this._frequencyData);
    
    const sampleRate = this._audioContext.sampleRate;
    const nyquist = sampleRate / 2;
    const binCount = this._frequencyData.length;
    const freqPerBin = nyquist / binCount;
    const cutoffBin = Math.floor(cutoffFreq / freqPerBin);
    
    let lowEnergy = 0;
    let highEnergy = 0;
    
    for (let i = 0; i < binCount; i++) {
      // Convert from dB to linear power
      const power = Math.pow(10, this._frequencyData[i] / 10);
      
      if (i < cutoffBin) {
        lowEnergy += power;
      } else {
        highEnergy += power;
      }
    }
    
    // Return large value if no low energy (unusual case)
    if (lowEnergy < 1e-10) return 1000;
    
    return highEnergy / lowEnergy;
  }

  /**
   * Detect Static (Low-band or Broadband)
   * Identifies static noise based on spectral pattern
   * 
   * Requirements:
   * - RMS must be >= -52 dB
   * - Must match either low-band OR broadband static pattern
   * 
   * Low-band static pattern:
   * Spectral flatness <= 0.15 AND Spectral rolloff <= 900 Hz AND High/low frequency ratio <= 0.08
   * Broadband static pattern:
   * Spectral flatness >= 0.18 AND Spectral rolloff >= 800 Hz AND Zero-crossing rate >= 0.08
   * 
   * @return {Object} { detected: boolean, type: 'low-band' | 'broadband' | null }
   */
  _detectStatic() {
    // Requires minimum RMS threshold
    if (this._db < -52) {
      return { detected: false, type: null };
    }
    
    // Get metrics needed for static detection
    const spectralFlatness = this._spectralFlatness;
    const spectralRolloff = this._spectralRolloff;
    const highLowRatio = this._highLowFreqRatio;
    const zcr = this._zcr; // Use general ZCR, not just during clipping
    // Check for low-band static
    const isLowBandStatic = (
      spectralFlatness <= 0.15 &&
      spectralRolloff <= 900 &&
      highLowRatio <= 0.08
    );
    // Check for broadband static
    const isBroadbandStatic = (
      spectralFlatness >= 0.18 &&
      spectralRolloff >= 800 &&
      zcr >= 0.08
    );
    
    if (isLowBandStatic || isBroadbandStatic) {
      return { detected: true, type: isLowBandStatic ? 'low-band' : 'broadband' };
    }

    return { detected: false, type: null };
  }

  /*
   * getters used to expose internal vars while avoiding issues when using with
   * a reactive store (e.g. vuex).
   */

  /**
   * Getter of recorder state. Based on MediaRecorder API.
   * @return {string} state of recorder (inactive | recording | paused)
   */
  get state() {
    return this._state;
  }

  /**
   * Getter of stream object. Based on MediaRecorder API.
   * @return {MediaStream} media stream object obtain from getUserMedia
   */
  get stream() {
    return this._stream;
  }

  get isMicQuiet() {
    return this._isMicQuiet;
  }

  get isMicMuted() {
    if(this._tracks && this._tracks[0]){
      return (this._tracks[0].muted || (this._tracks[0].getSettings().volume < 0.1))
    }
    else{
      return false;
    }
  }

  get isSilentRecording() {
    return this._isSilentRecording;
  }

  get isRecording() {
    return (this._state === 'recording');
  }

  /**
  * Getter of mic volume levels.
  */
  get volume() {
    return ({
      instant: this._instant, //Instantaneous rms value of the sample
      db: this._db,           //Instantaneous db value of the sample
      slow: this._slow,       //Smoothed value of db
      soft: this._soft,       //Boolean - true if audio is below the "soft" threshold
      loud: this._loud,       //Boolean - true if audio is above the "loud" threshold
      clip: this._clip,       //Number of samples that have hard clipped (>0.99)
      nearClip: this._nearClip, //Number of samples near clipping (>=0.8, matches backend)
      loudThreshold: this.loudThresholdDB, //Threshold for loud parameter
      softThreshold: this.softThresholdDB,  //Thershold for soft parameter
      maxVolume: this._maxVolume,
      minVolume: this._minVolume,
      maxClip: this._maxClip,
      // Simple intelligibility metrics (always available)
      zcr: this._zcr,                                       // General zero-crossing rate (0-1, typically 0.05-0.3)
      zcrDuringClipping: this._zcrDuringClipping,           // Zero-crossing rate during near-clipping (0-1, typically 0.05-0.3)
      spectralFlatness: this._spectralFlatness,             // Spectral flatness (0-1, typically 0.01-0.15)
      intelligibilitySeverity: this._intelligibilitySeverity, // Combined severity score (0-100, uses near-clipping)
      // Basic metrics (always available)
      peakAmplitude: this._peakAmplitude,               // Peak amplitude (0-1)
      crestFactorDB: this._crestFactorDB,               // Crest factor in dB (typically 6-20)
      spectralRolloff: this._spectralRolloff,           // Spectral rolloff in Hz (typically 3000-6000)
      highLowFreqRatio: this._highLowFreqRatio,         // High/low frequency ratio (typically 0.5-2.0)
      staticDetected: this._staticDetected,             // Static detected (boolean)
      staticType: this._staticType,                     // Static type ('low-band' | 'broadband' | null)
    });
  }

  get loggingInfo(){
    if (this._tracks && this._tracks[0]) {
      let returnValue = {label: this._tracks[0].label};
      returnValue.attributes = this._tracks[0].getSettings();
      return(returnValue);
    }
    return {label: 'unknown', attributes: {}};
  }

  get sampleRate(){
    return this._sampleRate;
  }

  
  get bufferLength(){
    return this._bufferLength;
  }

  /*
   * Private initializer of event target
   * Set event handlers that mimic MediaRecorder events plus others
   */

  // TODO make setters replace the listener insted of adding
  set onstart(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('start', cb.handler);
      this._onstartHandler = cb.handler;
    }
    else{
      this._eventTarget.removeEventListener('start',this._onstartHandler);
    }
  }
  set onstop(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('stop', cb.handler);
      this._onstopHandler = cb.handler;
    }
    else{
      this._eventTarget.removeEventListener('stop',this._onstopHandler);
    }
  }
  set ondataavailable(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('dataavailable', cb.handler);
      this._ondataavailableHandler = cb.handler;
    }
    else{
      this._eventTarget.removeEventListener('dataavailable',this._ondataavailableHandler);
    }
  }
  set onerror(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('error', cb.handler);
      this._onerrorHandler = cb.handler;
    }
    else{
      this._eventTarget.removeEventListener('error',this._onerrorHandler);
    }
  }
  set onstreamready(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('streamReady', cb.handler);
    }
    else{
      this._eventTarget.removeEventListener('streamReady',cb.handler);
    }
  }
  set onmute(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('mute', cb.handler);
    }
    else{
      this._eventTarget.removeEventListener('mute',cb.handler);
    }
  }
  set onunmute(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('unmute', cb.handler);
    }
    else{
      this._eventTarget.removeEventListener('unmute',cb.handler);
    }
  }
  set onsilentrecording(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('silentrecording', cb.handler);
    }
    else{
      this._eventTarget.removeEventListener('silentrecording',cb.handler);
    }
  }
  set onunsilentrecording(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('unsilentrecording', cb.handler);
    }
    else{
      this._eventTarget.removeEventListener('unsilentrecording',cb.handler);
    }
  }
  set onquiet(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('quiet', cb.handler);
      this._onquietHandler = cb.handler;
    }
    else{
      this._eventTarget.removeEventListener('quiet',this._onquietHandler);
    }
  }
  set onunquiet(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('unquiet', cb.handler);
      this._onunquietHandler = cb.handler;
    }
    else{
      this._eventTarget.removeEventListener('unquiet',this._onunquietHandler);
    }
  }
  set ontrackmuted(cb) {
    if(!cb.remove){
      this._eventTarget.addEventListener('trackmuted', cb.handler);
    }
    else{
      this._eventTarget.removeEventListener('trackmuted',cb.handler);
    }
  }
  set onaudiofileended(cb) { // this method is used to detect when the test audio file ends
    if(!cb.remove){
      this._eventTarget.addEventListener('audiofileended', cb.handler);
    }
    else{
      this._eventTarget.removeEventListener('audiofileended',cb.handler);
    }
  }
}
